import { useState, useEffect, useCallback } from 'react'
import { getLicitacoes, createLicitacao, updateLicitacao, deleteLicitacao } from '@/services/api'
import { formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Plus, Gavel, Trash2, Edit, Loader2, Filter, AlertTriangle, Clock, MapPin } from 'lucide-react'
import { toast } from 'sonner'

export default function LicitacoesPage() {
  const [licitacoes, setLicitacoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingLicitacao, setEditingLicitacao] = useState(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [formData, setFormData] = useState({
    numero_processo: '',
    objeto: '',
    validade: '',
    localizacao: ''
  })

  const fetchLicitacoes = useCallback(async () => {
    try {
      const response = await getLicitacoes(filterStatus || undefined)
      setLicitacoes(response.data)
    } catch (error) {
      toast.error('Erro ao carregar licitações')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  useEffect(() => {
    fetchLicitacoes()
  }, [fetchLicitacoes])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingLicitacao) {
        await updateLicitacao(editingLicitacao.id, formData)
        toast.success('Licitação atualizada com sucesso!')
      } else {
        await createLicitacao(formData)
        toast.success('Licitação criada com sucesso!')
      }
      
      setDialogOpen(false)
      setEditingLicitacao(null)
      setFormData({ numero_processo: '', objeto: '', validade: '', localizacao: '' })
      fetchLicitacoes()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar licitação')
    }
  }

  const handleEdit = (licitacao) => {
    setEditingLicitacao(licitacao)
    setFormData({
      numero_processo: licitacao.numero_processo,
      objeto: licitacao.objeto,
      validade: licitacao.validade.split('T')[0],
      localizacao: licitacao.localizacao,
      status: licitacao.status
    })
    setDialogOpen(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta licitação?')) return
    try {
      await deleteLicitacao(id)
      toast.success('Licitação excluída com sucesso!')
      fetchLicitacoes()
    } catch (error) {
      toast.error('Erro ao excluir licitação')
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateLicitacao(id, { status: newStatus })
      toast.success('Status atualizado com sucesso!')
      fetchLicitacoes()
    } catch (error) {
      toast.error('Erro ao atualizar status')
    }
  }

  const alertas = licitacoes.filter(l => l.alerta && l.status !== 'vencido')

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Licitações</h1>
          <p className="text-slate-500 mt-1">Gerencie os processos de licitação</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="nova-licitacao-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Licitação
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingLicitacao ? 'Editar Licitação' : 'Nova Licitação'}</DialogTitle>
              <DialogDescription>
                Preencha os dados da licitação
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="numero_processo">Número do Processo</Label>
                <Input
                  id="numero_processo"
                  value={formData.numero_processo}
                  onChange={(e) => setFormData({ ...formData, numero_processo: e.target.value })}
                  required
                  data-testid="input-numero"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="objeto">Objeto</Label>
                <Textarea
                  id="objeto"
                  value={formData.objeto}
                  onChange={(e) => setFormData({ ...formData, objeto: e.target.value })}
                  required
                  rows={3}
                  data-testid="input-objeto"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="validade">Validade</Label>
                <Input
                  id="validade"
                  type="date"
                  value={formData.validade}
                  onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                  required
                  data-testid="input-validade"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="localizacao">Localização</Label>
                <Input
                  id="localizacao"
                  value={formData.localizacao}
                  onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
                  placeholder="Ex: Arquivo, Setor X"
                  data-testid="input-localizacao"
                />
              </div>
              {editingLicitacao && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="vencido">Vencido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-licitacao-btn">
                  {editingLicitacao ? 'Atualizar' : 'Criar'} Licitação
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="alertas-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Atenção: {alertas.length} licitação(ões) próximas do vencimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alertas.map((alerta) => (
                <div key={alerta.id} className="p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">{alerta.numero_processo}</span>
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      {alerta.dias_para_vencer} dias
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2">{alerta.objeto}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-5 h-5 text-slate-400" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="filter-status">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="em_andamento">Em Andamento</SelectItem>
            <SelectItem value="concluido">Concluído</SelectItem>
            <SelectItem value="vencido">Vencido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : licitacoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Gavel className="w-12 h-12 mb-4 text-slate-300" />
              <p>Nenhuma licitação encontrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Número</TableHead>
                  <TableHead>Objeto</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Localização</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licitacoes.map((licitacao) => (
                  <TableRow key={licitacao.id} className="hover:bg-slate-50" data-testid={`licitacao-row-${licitacao.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {licitacao.numero_processo}
                        {licitacao.alerta && licitacao.status !== 'vencido' && (
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{licitacao.objeto}</TableCell>
                    <TableCell>
                      <div>
                        <span>{formatDate(licitacao.validade)}</span>
                        {licitacao.dias_para_vencer > 0 && (
                          <p className="text-xs text-slate-500">{licitacao.dias_para_vencer} dias restantes</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(licitacao.status)} border`}>
                        {getStatusLabel(licitacao.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {licitacao.localizacao ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <MapPin className="w-3 h-3" />
                          <span className="text-sm">{licitacao.localizacao}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(licitacao)} data-testid={`btn-edit-${licitacao.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(licitacao.id)} data-testid={`btn-delete-${licitacao.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
